import React, { useEffect, useState, useRef, useLayoutEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { ArrowBack } from '@mui/icons-material';
import { QRCodeCanvas } from 'qrcode.react';
import { addDoc, collection, doc, setDoc, serverTimestamp, getDoc, arrayUnion } from 'firebase/firestore';
import { db } from '../../utils/firebase';
import styles from './StrokeCheckResults.module.css';
import strokeData from '../../data/stroke.json';
import { getUserId } from '../../utils/userId';

interface Answer {
  questionId: string;
  optionId: string;
  value: boolean | number | string;
}

interface LocationState {
  answers: Answer[];
  riskFactors: {
    keyword: string;
    description: string;
  }[];
  qaRiskFactors: {
    keyword: string;
    description: string;
  }[];
  totalQuestions: number;
  allQuestions?: any[];
  sessionId?: string | null;
}

interface RiskFactor {
  keyword: string;
  description: string;
}

interface Question {
  id: string;
  type: string;
  questionTextKey: string;
  options: {
    optionTextKey: string;
    value: boolean | number | string;
  }[];
  riskFactor?: RiskFactor;
  risk_trigger_values?: (boolean | number | string)[];
}

interface FormattedRiskFactor {
  keyword: string;
  description: string;
}

interface StrokeResponseData {
  userId: string;
  timestamp: any;
  answers: any[];
  riskFactors: FormattedRiskFactor[];
  qaRiskFactors?: FormattedRiskFactor[];
  totalRiskCount: number;
  totalQuestions: number;
  shareDocId?: string;
}

const StrokeCheckResults: React.FC = () => {
  const { t, ready } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { id, hospitalId } = useParams<{ id: string; hospitalId?: string }>();
  const prefix = hospitalId ? `/${hospitalId}` : '';
  const [savingToFirebase, setSavingToFirebase] = useState(false);
  const [shareDocId, setShareDocId] = useState<string | null>(null);
  // qr 에러 확인
  const [qrError, setQrError] = useState<string | null>(null);

  const [result, setResult] = React.useState<any>(null); // 이전 기록이 있는 테스트
  const locationState = location.state as LocationState | null; // 새 테스트
  
  const [rawAnswers, setRawAnswers] = useState(locationState?.answers ?? null);
  const [rawRiskFactors, setRawRiskFactors] = useState(locationState?.riskFactors ?? null);

  const [rawQaRiskFactors, setRawQaRiskFactors] = useState(locationState?.qaRiskFactors ?? null);

  const [displayRiskFactors, setDisplayRiskFactors] = useState<FormattedRiskFactor[]>([]);

  const contentRef = useRef<HTMLDivElement>(null);
  const scrollTimerRef = useRef<NodeJS.Timeout | undefined>(undefined);

  useLayoutEffect(() => {
    document.documentElement.style.overflow = 'auto';
    document.body.style.overflow = 'auto';

    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;

    if (contentRef.current) {
      contentRef.current.scrollTop = 0;
    }

    const timer = setTimeout(() => {
      document.documentElement.style.overflow = '';
      document.body.style.overflow = '';
    }, 50);

    return () => clearTimeout(timer);
  }, []);
  
  
  const saveToFirebase = async () => {
    if (!ready) return; // i18n 준비 안 되면 대기
    if (!rawAnswers || !rawRiskFactors || !rawQaRiskFactors || !locationState?.allQuestions || !locationState?.totalQuestions) {
      console.warn("saveToFirebase: 필요한 데이터가 locationState에 없습니다.");
      return;
    }

    setSavingToFirebase(true);
    setQrError(null);

    const allQuestions = locationState.allQuestions; 
    const totalQuestions = locationState.totalQuestions;

    try {
      const userId = getUserId();
      const sessionId = locationState.sessionId;
      if (!userId) {
        setQrError('사용자 ID가 없습니다.');
        return;
      }

      console.log('✅ Firestore 저장 시작...');

      // 포맷팅
      const formattedAnswers = rawAnswers.map((answer: any) => {
        const question = allQuestions.find((q: any) => q.id === answer.questionId);
        const textKey = question ? (question.questionTextKey || question.textKey) : '';
        return {
          questionId: answer.questionId,
          questionText: textKey ? t(textKey) : '',
          answer: answer.value,
          answerText:
            typeof answer.value === 'boolean'
              ? (answer.value ? t('common_yes') : t('common_no'))
              : String(answer.value),
        };
      });

      const formattedRiskFactors = rawRiskFactors.map((factor: any) => ({
        keyword: t(factor.keyword),
        description: t(factor.description),
      }));

      const formattedQaRiskFactors = rawQaRiskFactors.map((factor: any) => ({
        keyword: t(factor.keyword),
        description: t(factor.description),
      }));

      // 공유용 문서 생성
      const sharePayload = {
        kind: 'stroke',
        createdAt: serverTimestamp(),
        score: rawRiskFactors.length,
        riskFactors: formattedRiskFactors,
        qaRiskFactors: formattedQaRiskFactors,
      };

      const shareRef = await addDoc(collection(db, 'sharedStrokeResults'), sharePayload);
      console.log('✅ Firestore addDoc 성공:', shareRef.id);
      setShareDocId(shareRef.id);

      if (sessionId) {
      const reportRef = doc(db, "reports", sessionId);
      await setDoc(reportRef, {
        hospitalId: hospitalId || "default", // 필터링을 위한 병원 ID 저장
        patientId: userId,
        timestamp: serverTimestamp(),
        strokeReportId: shareRef.id, // 뇌졸중 리포트 링크
        checkTypes: arrayUnion("Stroke") // 관리자 목록 배지용 태그 추가
      }, { merge: true });
    }

      // 원본 결과 저장
      const responseData = {
        userId,
        timestamp: serverTimestamp(),
        answers: formattedAnswers,
        riskFactors: formattedRiskFactors,
        qaRiskFactors: formattedQaRiskFactors,
        totalRiskCount: rawRiskFactors.length,
        totalQuestions: totalQuestions,
        shareDocId: shareRef.id,
      };

      const docId = `${userId}_${Date.now()}`;
      await setDoc(doc(db, 'userStrokeResponse', docId), responseData, { merge: true });
    } catch (err: any) {
      console.error('❌ Firestore 저장 오류:', err);
      setQrError(err?.message || 'QR 생성 실패');
    } finally {
      setSavingToFirebase(false);
    }
  };

  useEffect(() => {
    const fetchResult = async () => {
      if (id && !locationState) {
        const docRef = doc(db, 'userStrokeResponse', id);
        const snapshot = await getDoc(docRef);
        
        if (!snapshot.exists()) {
          setQrError("원본 테스트 결과를 찾을 수 없습니다.");
          return;
        }
        const data = snapshot.data() as StrokeResponseData;
        setResult(data);

        setDisplayRiskFactors(data.riskFactors || []);
        if(data.shareDocId) {
          console.log("기존 QR ID를 찾았습니다:", data.shareDocId)
          setShareDocId(data.shareDocId);
        } else {
          if (!ready) {
            console.warn('i18n not ready, deferring on-the-fly QR generation...');
            return;
          }

          console.warn("기존 QR ID가 없습니다. 새로 생성합니다...");
          setSavingToFirebase(true);
          setQrError(null);

          try {
            const reTranslatedRiskFactors = data.riskFactors.map((rf: FormattedRiskFactor) => ({
              keyword: t(rf.keyword),
              description: t(rf.description)
            }))

            const sharePayload = {
              kind: 'stroke',
              createdAt: data.timestamp,
              score: data.totalRiskCount,
              riskFactors: reTranslatedRiskFactors,
            };

            const shareRef = await addDoc(collection(db, 'sharedStrokeResults'), sharePayload);
            console.log('Firestore on-the-fly addDoc 성공:', shareRef.id);

            setShareDocId(shareRef.id);

            await setDoc(docRef, {
              shareDocId: shareRef.id
            }, {merge: true});
          } catch (err: any) {
            console.error("On-the-fly QR 생성 실패:", err);
            setQrError("이전 리포트의 QR 생성에 실패했습니다.");
          } finally {
            setSavingToFirebase(false);
          }
        }
      }
      else if (locationState) {
        const formatted = locationState.riskFactors.map((factor: RiskFactor) => ({
          keyword: t(factor.keyword),
          description: t(factor.description),
        }));
        setDisplayRiskFactors(formatted);
      }
    };
    fetchResult();
  }, [id, locationState, t, ready]);

  useEffect(() => {
    if (ready && locationState && !shareDocId) {
      console.log('🌍 i18n 준비 완료 → Firestore 저장 재시도');
      saveToFirebase();
    }
  }, [ready]);

  useEffect(() => {
    console.log('🧩 shareDocId 업데이트됨:', shareDocId);
  }, [shareDocId]);

  useEffect(() => {
    const contentElement = contentRef.current;
    if (!contentElement) return;

    const handleScroll = () => {
      contentElement.classList.remove(styles.scrollHidden);
      
      clearTimeout(scrollTimerRef.current);

      scrollTimerRef.current = setTimeout(() => {
        contentElement.classList.add(styles.scrollHidden);
      }, 400); 
    };

    contentElement.addEventListener('scroll', handleScroll);
    contentElement.classList.add(styles.scrollHidden);

    return () => {
      contentElement.removeEventListener('scroll', handleScroll);
      clearTimeout(scrollTimerRef.current);
    };
  }, [contentRef]);
  
  const handleBack = () => {
    navigate(`${prefix}/stroke_check`, { 
      state: { 
        answers: rawAnswers
      }
    });
  };

  const handleConfirm = () => {
    localStorage.removeItem('strokeAnswers');
    navigate(`${prefix}/stroke_check`);
  };

  const getShareOrigin = () => {
    return window.location.origin;
  };

  const publicOrigin = process.env.REACT_APP_PUBLIC_BASE_URL || window.location.origin || "http://localhost:3000";
  const shareUrl = shareDocId ? `${publicOrigin}${prefix}/stroke/report/${shareDocId}` : '';

  console.log("✅ shareDocId:", shareDocId);
  console.log("✅ shareUrl:", shareUrl);

  useEffect(() => {
    console.log("🧠 QR 렌더링 조건:", !!shareUrl);
    console.log("shareUrl 값:", shareUrl);
    console.log("QRCodeCanvas 타입:", typeof QRCodeCanvas);
  }, [shareUrl]);

  useEffect(() => {
  if (shareUrl) {
    console.log("🧩 QR 렌더링 useEffect 감지됨:", shareUrl);
    setTimeout(() => {
      const qr = document.querySelector('.' + styles.qrCodeContainer + ' svg');
      console.log('🎯 실제 DOM에서 QR SVG 탐색 결과:', qr);
    }, 1000);
  }
}, [shareUrl]);

const score = displayRiskFactors.length;
let total: number;

if (result) {
    total = result.totalQuestions || 23; 
} else if (locationState) { 
    total = locationState.totalQuestions;
} else {
  total = 23; 
}

const percentage = total === 0 ? 0 : (score / total) * 100;

let scoreClass = styles.scoreGreen; 
if (percentage >= 60) { 
  scoreClass = styles.scoreRed;
} else if (percentage >= 30) { 
  scoreClass = styles.scoreOrange;
}

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <button className={styles.backButton} onClick={handleBack}>
          <ArrowBack />
        </button>
        <span className={styles.headerText}>{t('check_before_2')}</span>
      </div>
      <div className={styles.content}>
      <div className={styles.contentScroll} ref={contentRef}>
        <div className={styles.imageContainer}>
          <span className={styles.resultTitle}>{t('test_result_title')}</span>
        </div>

        <div className={styles.historyScore}>
          <div className={`${styles.scoreCircle} ${scoreClass}`}>
            {score}
          </div>
          <span className={styles.scoreTotalText}>
            / {total}
          </span>
        </div>

        <div className={styles.resultDescription}>{t('test_result_description')}</div>

        {displayRiskFactors.map((rf: FormattedRiskFactor, i: number) => (
          <div key={i} className={styles.riskFactorContainer}>
            <div className={styles.riskFactorKeyword}>{t(rf?.keyword || '')}</div>
            <div className={styles.riskFactorDescription}>{t(rf?.description || '')}</div>
          </div>
        ))}

        {/* QR 코드 영역 */}
        <div className={styles.qrCodeContainer}>
          {shareUrl ? (
            <>
            {console.log("QR 렌더링 시점:", shareDocId, shareUrl)}
            <div className={styles.qrWrapper}>
              <QRCodeCanvas
                key={shareUrl}
                value={shareUrl}
                size={180}
                bgColor="#FFFFFF"
                fgColor="#000000"
                includeMargin
                style={{
                  borderRadius: '8px',
                  border: '1px solid #ccc',
                  boxShadow: '0 0 8px rgba(0,0,0,0.15)',
                }}
              />
              </div>
              <div className={styles.qrCodeDescription}>{t('scan_qr_code')}</div>
            </>
          ) : (
            <>
              <div
                style={{
                  height: 180,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  opacity: 0.7,
                }}
              >
                {t('generating_qr') || 'QR 생성 중...'}
              </div>

              {qrError && (
                <>
                  <div style={{ fontSize: 12, opacity: 0.8 }}>({qrError})</div>
                  <button
                    className={styles.copyLinkButton}
                    onClick={() => window.location.reload()}
                    style={{ marginTop: 4 }}
                  >
                    다시 시도
                  </button>
                </>
              )}
            </>
          )}
        </div>
      </div>
      </div>

      <button
        className={styles.confirmButton}
        onClick={handleConfirm}
        disabled={savingToFirebase}
      >
        {savingToFirebase ? t("saving") : t("confirm_button_text")}
      </button>
    </div>
  );
};

export default StrokeCheckResults; 